import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Default 4-corner points
const DEFAULT_POINTS = [
  { x: 0,   y: 0,   color: '#6366f1' }, // top-left
  { x: 100, y: 0,   color: '#ec4899' }, // top-right
  { x: 0,   y: 100, color: '#06b6d4' }, // bottom-left
  { x: 100, y: 100, color: '#f59e0b' }, // bottom-right
];

const BLEND_MODES = [
  { value: 'screen',   label: 'Screen' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'overlay',  label: 'Overlay' },
  { value: 'normal',   label: 'Normal' },
];

// ─── Canvas renderer ─────────────────────────────────────────────────────────

function renderGradientToCanvas(canvas, points, baseColor, blendMode, spread) {
  const ctx = canvas.getContext('2d');
  const { width, height } = canvas;
  ctx.clearRect(0, 0, width, height);

  // Base fill
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, width, height);

  // Each point: radial gradient
  for (const pt of points) {
    const cx = (pt.x / 100) * width;
    const cy = (pt.y / 100) * height;
    const radius = (spread / 100) * Math.max(width, height);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grad.addColorStop(0, pt.color + 'dd');
    grad.addColorStop(1, pt.color + '00');
    ctx.globalCompositeOperation =
      blendMode === 'screen'   ? 'screen'   :
      blendMode === 'multiply' ? 'multiply' :
      blendMode === 'overlay'  ? 'overlay'  : 'source-over';
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.globalCompositeOperation = 'source-over';
}

// ─── MeshGradientEditor ──────────────────────────────────────────────────────

/**
 * Self-contained mesh gradient editor. Renders as an inline popover in the
 * Colors sidebar section.
 *
 * Props:
 *   projectId       — for API calls
 *   selectedElement — from ReviewStage (used for "Apply to element")
 *   currentPageSlug — current page slug
 *   meshGradients   — { [css_path]: config } from magic_state.review_editor
 *   onApplied       — called with newMagicState after successful apply
 *   onClose         — close the editor popover
 */
export default function MeshGradientEditor({
  projectId,
  selectedElement,
  currentPageSlug = 'home',
  meshGradients = {},
  onApplied,
  onClose,
}) {
  const canvasRef = useRef(null);
  const [points, setPoints] = useState(DEFAULT_POINTS);
  const [baseColor, setBaseColor] = useState('#0a0a0f');
  const [blendMode, setBlendMode] = useState('screen');
  const [spread, setSpread] = useState(60);
  const [selectedHandle, setSelectedHandle] = useState(null); // index of handle whose picker is open
  const [applying, setApplying] = useState(null); // 'element' | 'body' | 'remove'

  // Re-render canvas whenever state changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderGradientToCanvas(canvas, points, baseColor, blendMode, spread);
  }, [points, baseColor, blendMode, spread]);

  const handlePointColorChange = useCallback((idx, color) => {
    setPoints(prev => prev.map((p, i) => i === idx ? { ...p, color } : p));
  }, []);

  const applyGradient = useCallback(async (targetPath) => {
    if (!projectId || applying) return;
    setApplying(targetPath === null ? 'remove' : targetPath === 'body' ? 'body' : 'element');

    try {
      const body = {
        page_slug: currentPageSlug,
        css_path: targetPath,
        gradient_config: targetPath === null ? null : {
          points,
          base_color: baseColor,
          blend_mode: blendMode,
          spread,
        },
      };

      const resp = await axios.post(
        `${API_URL}/magic/projects/${projectId}/set-mesh-gradient`,
        body
      );
      toast.success(targetPath === null ? 'Gradient removed' : 'Mesh gradient applied');
      if (onApplied) onApplied(resp.data?.magic_state);
    } catch (err) {
      toast.error('Failed to apply gradient');
    } finally {
      setApplying(null);
    }
  }, [projectId, currentPageSlug, points, baseColor, blendMode, spread, applying, onApplied]);

  const hasSelectedElement = !!selectedElement?.cssPath;
  const currentPath = selectedElement?.cssPath || null;
  const existingGradientForEl = currentPath && meshGradients[currentPath];
  const existingBodyGradient = meshGradients['body'];

  return (
    <div
      className="rounded-xl p-3 space-y-3"
      style={{ background: 'var(--orbis-s2)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium" style={{ color: 'var(--orbis-t1)' }}>
          Mesh Gradient
        </span>
        <button onClick={onClose} className="w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--orbis-s3)] transition-colors">
          <X className="w-3 h-3" style={{ color: 'var(--orbis-t4)' }} />
        </button>
      </div>

      {/* Canvas preview */}
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={220}
          height={120}
          className="w-full rounded-lg"
          style={{ display: 'block', border: '1px solid rgba(255,255,255,0.06)' }}
        />
        {/* Corner handles */}
        {points.map((pt, idx) => (
          <button
            key={idx}
            onClick={() => setSelectedHandle(selectedHandle === idx ? null : idx)}
            className="absolute w-4 h-4 rounded-full border-2 border-white/70 transition-all hover:scale-110"
            style={{
              backgroundColor: pt.color,
              left: pt.x === 0 ? 6 : undefined,
              right: pt.x === 100 ? 6 : undefined,
              top: pt.y === 0 ? 6 : undefined,
              bottom: pt.y === 100 ? 6 : undefined,
              boxShadow: selectedHandle === idx
                ? '0 0 0 2px var(--orbis-blue)'
                : '0 1px 4px rgba(0,0,0,0.5)',
            }}
          />
        ))}
      </div>

      {/* Handle color picker */}
      {selectedHandle !== null && (
        <div className="flex items-center gap-2">
          <div className="relative w-7 h-7 rounded overflow-hidden border border-[rgba(255,255,255,0.12)] shrink-0">
            <input
              type="color"
              value={points[selectedHandle]?.color || '#ffffff'}
              onChange={e => handlePointColorChange(selectedHandle, e.target.value)}
              className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
            />
            <div className="w-full h-full" style={{ backgroundColor: points[selectedHandle]?.color }} />
          </div>
          <span className="text-[10px]" style={{ color: 'var(--orbis-t3)' }}>
            Corner {selectedHandle + 1} color
          </span>
        </div>
      )}

      {/* Base color + blend mode row */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative w-5 h-5 rounded overflow-hidden border border-[rgba(255,255,255,0.12)] shrink-0">
            <input
              type="color"
              value={baseColor}
              onChange={e => setBaseColor(e.target.value)}
              className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
            />
            <div className="w-full h-full" style={{ backgroundColor: baseColor }} />
          </div>
          <span className="text-[10px]" style={{ color: 'var(--orbis-t3)' }}>Base color</span>
        </div>

        {/* Blend mode */}
        <div className="flex gap-1">
          {BLEND_MODES.map(m => (
            <button
              key={m.value}
              onClick={() => setBlendMode(m.value)}
              className="flex-1 text-[9px] py-1 rounded transition-colors"
              style={{
                background: blendMode === m.value ? 'var(--orbis-blue)' : 'var(--orbis-s3)',
                color: blendMode === m.value ? 'var(--orbis-bg)' : 'var(--orbis-t3)',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Spread slider */}
        <div className="flex items-center gap-2">
          <span className="text-[10px] w-10 shrink-0" style={{ color: 'var(--orbis-t3)' }}>Spread</span>
          <input
            type="range"
            min={30}
            max={90}
            value={spread}
            onChange={e => setSpread(Number(e.target.value))}
            className="flex-1 h-1 rounded appearance-none cursor-pointer"
            style={{ accentColor: 'var(--orbis-blue)' }}
          />
          <span className="text-[10px] w-6 text-right tabular-nums" style={{ color: 'var(--orbis-t2)' }}>
            {spread}%
          </span>
        </div>
      </div>

      {/* Apply buttons */}
      <div className="space-y-1.5">
        {hasSelectedElement && (
          <button
            onClick={() => applyGradient(currentPath)}
            disabled={!!applying}
            className="w-full text-[11px] py-1.5 rounded-md transition-colors flex items-center justify-center gap-1.5"
            style={{ background: 'var(--orbis-blue)', color: 'var(--orbis-bg)', opacity: applying ? 0.6 : 1 }}
          >
            {applying === 'element'
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Applying...</>
              : existingGradientForEl ? 'Update on element' : 'Apply to element'}
          </button>
        )}

        <button
          onClick={() => applyGradient('body')}
          disabled={!!applying}
          className="w-full text-[11px] py-1.5 rounded-md transition-colors flex items-center justify-center gap-1.5"
          style={{
            background: 'var(--orbis-s3)',
            color: 'var(--orbis-t2)',
            opacity: applying ? 0.6 : 1,
          }}
        >
          {applying === 'body'
            ? <><Loader2 className="w-3 h-3 animate-spin" /> Applying...</>
            : existingBodyGradient ? 'Update page background' : 'Apply as page background'}
        </button>

        {(existingGradientForEl || existingBodyGradient) && (
          <button
            onClick={() => applyGradient(null)}
            disabled={!!applying}
            className="w-full text-[10px] py-1 rounded-md transition-colors flex items-center justify-center gap-1"
            style={{ color: 'var(--orbis-t4)' }}
          >
            {applying === 'remove'
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Removing...</>
              : <><X className="w-3 h-3" /> Remove gradient</>}
          </button>
        )}
      </div>

      <p className="text-[9px]" style={{ color: 'var(--orbis-t4)' }}>
        Click a corner handle to change its color. Undo with ⌘Z.
      </p>
    </div>
  );
}
